import { useEffect, useRef, useState } from 'react';

import type { TopOfBook } from '../types/entry.types';

/**
 * How long a quote cell keeps rendering its last-good top-of-book after the
 * live side momentarily empties (`top() === null`). Thin, quiet markets — the
 * pre-season friendlies, live exact-score submarkets — routinely have a single
 * flickering level on a side, so a level being pulled/filled blanks that side
 * for a few hundred ms before the next order posts. Without a hold the cell
 * flaps value ↔ "—" several times a second (the reported "titileo"). This
 * horizon absorbs those sub-second flaps while still surfacing a genuinely
 * empty side once it has stayed empty past the window.
 */
export const DEFAULT_QUOTE_HOLD_MS = 2000;

export interface HeldQuote {
  /** The value to render: the live top, or the held last-good during a flap. */
  value: TopOfBook | null;
  /** True while `value` is a held last-good (the live side is currently empty). */
  stale: boolean;
}

/** Per-side memory carried across renders (one entry per quote key). */
export interface HeldSlot {
  lastGood: TopOfBook | null;
  blankedAt: number | null;
}

export interface HeldStep {
  quote: HeldQuote;
  /** Wall-clock ms at which the hold expires, or `null` when nothing is held. */
  expiry: number | null;
}

export function makeHeldSlot(): HeldSlot {
  return { lastGood: null, blankedAt: null };
}

/**
 * Pure hold decision for ONE quote side. Mutates `slot` in place (it is the
 * cross-render memory) and returns what to render plus the hold expiry.
 *
 * - `active === false`: pass through `null` and forget any held value — the
 *   caller's own "—" gate (the per-pair kill switch) stays authoritative.
 * - live value present: render it, `stale: false`, remember it as last-good.
 * - live value empty with a last-good younger than `holdMs`: render the
 *   last-good, `stale: true`, and report its expiry.
 * - live value empty past `holdMs` (or never seen): render `null`,
 *   `stale: false`, and forget the held value.
 */
export function stepHeldSlot(
  slot: HeldSlot,
  value: TopOfBook | null | undefined,
  active: boolean,
  holdMs: number,
  now: number
): HeldStep {
  if (!active) {
    slot.lastGood = null;
    slot.blankedAt = null;
    return { quote: { value: null, stale: false }, expiry: null };
  }

  if (value != null) {
    slot.lastGood = value;
    slot.blankedAt = null;
    return { quote: { value, stale: false }, expiry: null };
  }

  // The live side is empty.
  if (slot.lastGood == null) {
    return { quote: { value: null, stale: false }, expiry: null };
  }
  if (slot.blankedAt == null) slot.blankedAt = now;
  const expiry = slot.blankedAt + holdMs;
  if (now < expiry) {
    return { quote: { value: slot.lastGood, stale: true }, expiry };
  }
  slot.lastGood = null;
  slot.blankedAt = null;
  return { quote: { value: null, stale: false }, expiry: null };
}

/**
 * Hold the last-good top-of-book for each named quote side across a transient
 * empty-side flap, mirroring how the venue-position cells hold their last frame
 * across a reconnect instead of blanking. Purely a DISPLAY concern: the store
 * still carries the raw truth (a `null` side really is empty), and the engine's
 * pricing/hedging is unaffected — this only decides what the cell renders.
 *
 * A single timer re-renders the hook at the nearest hold expiry so a row that
 * stops ticking entirely still drops its held value on time.
 */
export function useHeldQuotes<K extends string>(
  current: Record<K, TopOfBook | null | undefined>,
  active: boolean,
  holdMs: number = DEFAULT_QUOTE_HOLD_MS,
  now: () => number = Date.now
): Record<K, HeldQuote> {
  const slots = useRef<Map<K, HeldSlot>>(new Map());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, forceTick] = useState(0);

  const t = now();
  const out = {} as Record<K, HeldQuote>;
  let nextExpiry = Number.POSITIVE_INFINITY;

  for (const key of Object.keys(current) as K[]) {
    let slot = slots.current.get(key);
    if (!slot) {
      slot = makeHeldSlot();
      slots.current.set(key, slot);
    }
    const step = stepHeldSlot(slot, current[key], active, holdMs, t);
    out[key] = step.quote;
    if (step.expiry != null) nextExpiry = Math.min(nextExpiry, step.expiry);
  }

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (nextExpiry !== Number.POSITIVE_INFINITY) {
      const delay = Math.max(0, nextExpiry - now()) + 1;
      timer.current = setTimeout(() => forceTick(x => x + 1), delay);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
    // Re-arm only when the nearest expiry moves; a value returning sets it to
    // Infinity and clears the pending timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextExpiry]);

  return out;
}
